import React, { useEffect, useMemo, useRef, useState } from 'react';
import styled, { css } from 'styled-components';
import cookieConsentService, {
  COOKIE_CONSENT_CHANGED_EVENT,
  COOKIE_MANAGER_OPEN_EVENT
} from '../../services/cookieConsentService';

const SITE_NAME = process.env.REACT_APP_SITE_NAME;

const Banner = styled.aside`
  position: fixed;
  left: max(1rem, env(safe-area-inset-left));
  right: max(1rem, env(safe-area-inset-right));
  bottom: max(1rem, env(safe-area-inset-bottom));
  z-index: 3000;
  border-radius: 14px;
  background: linear-gradient(145deg, #13293d 0%, #1f3d58 100%);
  color: #f3f7fb;
  box-shadow: 0 16px 40px rgba(6, 24, 38, 0.35);
  border: 1px solid rgba(255, 255, 255, 0.15);
  max-height: calc(100dvh - max(1rem, env(safe-area-inset-top)) - max(1rem, env(safe-area-inset-bottom)));
  overflow: auto;

  @media (max-width: 600px) {
    left: env(safe-area-inset-left);
    right: env(safe-area-inset-right);
    bottom: env(safe-area-inset-bottom);
    border-radius: 14px 14px 0 0;
    max-height: calc(100dvh - env(safe-area-inset-top));
  }
`;

const BannerInner = styled.div`
  max-width: 1100px;
  margin: 0 auto;
  padding: 1rem 1.25rem;
  display: grid;
  grid-template-columns: 1fr auto;
  gap: 1rem;
  align-items: start;

  @media (max-width: 880px) {
    grid-template-columns: 1fr;
  }

  @media (max-width: 560px) {
    padding: 0.9rem 1rem;
    gap: 0.8rem;
  }
`;

const BannerTitle = styled.h3`
  margin: 0 0 0.35rem;
  font-size: 1rem;
  font-weight: 700;
`;

const BannerText = styled.p`
  margin: 0;
  color: #d8e7f3;
  font-size: 0.9rem;
  line-height: 1.5;
`;

const BannerActions = styled.div`
  display: flex;
  gap: 0.6rem;
  align-items: center;
  justify-content: flex-end;
  flex-wrap: wrap;

  @media (max-width: 560px) {
    width: 100%;
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 0.45rem;

    > button {
      width: 100%;
      min-height: 40px;
      font-size: 0.78rem;
      padding: 0.45rem 0.55rem;
    }

    > button:last-child {
      grid-column: 1 / -1;
    }
  }
`;

const ActionButton = styled.button`
  border: 1px solid ${({ $variant }) => ($variant === 'ghost' ? 'rgba(255, 255, 255, 0.3)' : 'transparent')};
  border-radius: 10px;
  font-size: 0.9rem;
  font-weight: 600;
  padding: 0.7rem 1.15rem;
  min-height: 48px;
  cursor: pointer;
  touch-action: manipulation;
  transition: border-color 0.2s ease, background 0.2s ease;
  background: ${({ $variant }) => {
    if ($variant === 'ghost') return 'transparent';
    if ($variant === 'secondary') return '#f4b400';
    return '#57b84d';
  }};
  color: ${({ $variant }) => ($variant === 'ghost' ? '#ffffff' : '#13293d')};
  box-shadow: none;

  &:hover {
    background: ${({ $variant }) => {
      if ($variant === 'ghost') return 'rgba(255, 255, 255, 0.06)';
      if ($variant === 'secondary') return '#e0a700';
      return '#4ea746';
    }};
    border-color: ${({ $variant }) => ($variant === 'ghost' ? 'rgba(255, 255, 255, 0.42)' : 'transparent')};
  }

  @media (max-width: 560px) {
    min-height: 42px;
    font-size: 0.82rem;
    padding: 0.52rem 0.72rem;
  }
`;

const Overlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(10, 20, 30, 0.6);
  z-index: 3200;
  padding:
    max(1rem, env(safe-area-inset-top))
    max(1rem, env(safe-area-inset-right))
    max(1rem, env(safe-area-inset-bottom))
    max(1rem, env(safe-area-inset-left));
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  overscroll-behavior: contain;
  touch-action: none;

  @media (max-width: 640px) {
    align-items: stretch;
    justify-content: stretch;
    padding: 0;
  }
`;

const ModalPanel = styled.div`
  width: 100%;
  max-width: 620px;
  max-height: 100%;
  background: #ffffff;
  border-radius: 14px;
  box-shadow: 0 24px 70px rgba(14, 32, 48, 0.25);
  position: relative;
  z-index: 3201;
  border: 1px solid #dbe5ef;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  overscroll-behavior: contain;
  touch-action: pan-y;

  @media (max-width: 640px) {
    width: 100%;
    max-width: 100%;
    height: 100dvh;
    max-height: 100dvh;
    min-height: 100dvh;
    border-radius: 0;
  }
`;

const ModalHeader = styled.div`
  padding: 1rem 1.25rem;
  border-bottom: 1px solid #e3ebf3;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  background: #ffffff;
  position: sticky;
  top: 0;
  z-index: 2;

  @media (max-width: 640px) {
    padding: 0.75rem 0.85rem;
  }
`;

const ModalTitle = styled.h2`
  margin: 0;
  font-size: 1.1rem;
  color: #11283d;
`;

const CloseButton = styled.button`
  border: none;
  background: transparent;
  font-size: 1.2rem;
  color: #6b7d8f;
  cursor: pointer;
  line-height: 1;
  min-width: 40px;
  min-height: 40px;
  border-radius: 8px;

  &:hover {
    background: #f2f7fc;
  }
`;

const ModalBody = styled.div`
  padding: 1rem 1.25rem;
  display: grid;
  gap: 0.9rem;
  min-height: 0;
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
  overscroll-behavior: contain;
  touch-action: pan-y;

  @media (max-width: 640px) {
    padding: 0.75rem 0.85rem;
    gap: 0.75rem;
  }
`;

const IntroText = styled.p`
  margin: 0;
  color: #3d5469;
  line-height: 1.5;
  font-size: 0.92rem;
`;

const CookieRow = styled.label`
  border: 1px solid #dbe5ef;
  background: #f8fbfe;
  border-radius: 10px;
  padding: 0.85rem;
  display: grid;
  grid-template-columns: 1fr auto;
  gap: 0.8rem;
  align-items: start;
  cursor: pointer;

  @media (max-width: 560px) {
    gap: 0.6rem;
    padding: 0.72rem;
  }
`;

const CookieTitle = styled.span`
  display: block;
  font-weight: 700;
  color: #132b40;
  margin-bottom: 0.2rem;
`;

const CookieDescription = styled.span`
  display: block;
  color: #496174;
  font-size: 0.86rem;
  line-height: 1.45;
`;

const CookieToggle = styled.input`
  appearance: none;
  -webkit-appearance: none;
  width: 46px;
  height: 28px;
  border-radius: 999px;
  border: 1px solid #becddd;
  background: #cad8e5;
  justify-self: end;
  position: relative;
  cursor: pointer;
  transition: background 0.2s ease, border-color 0.2s ease;

  &::after {
    content: '';
    position: absolute;
    top: 2px;
    left: 2px;
    width: 22px;
    height: 22px;
    border-radius: 50%;
    background: #ffffff;
    box-shadow: 0 1px 3px rgba(17, 40, 61, 0.22);
    transition: transform 0.2s ease;
  }

  &:checked {
    background: #2f8a53;
    border-color: #2f8a53;
  }

  &:checked::after {
    transform: translateX(18px);
  }

  &:disabled {
    opacity: 0.95;
    cursor: not-allowed;
    background: #d3dfe9;
    border-color: #c4d2df;
  }

  @media (max-width: 560px) {
    justify-self: end;
  }
`;

const UpdatedAt = styled.p`
  margin: 0.2rem 0 0;
  color: #6a7f92;
  font-size: 0.8rem;
`;

const ModalFooter = styled.div`
  padding: 1rem 1.25rem;
  border-top: 1px solid #e3ebf3;
  display: flex;
  justify-content: space-between;
  gap: 0.8rem;
  flex-wrap: wrap;
  background: #ffffff;
  position: sticky;
  bottom: 0;
  z-index: 2;

  @media (max-width: 640px) {
    padding: 0.75rem 0.85rem max(0.75rem, env(safe-area-inset-bottom));
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 0.5rem;
  }
`;

const ModalAction = styled.button`
  border: 1px solid #cad8e6;
  border-radius: 8px;
  padding: 0.6rem 0.95rem;
  min-height: 44px;
  font-size: 0.9rem;
  font-weight: 600;
  cursor: pointer;
  touch-action: manipulation;
  background: #ffffff;
  color: #18344b;
  box-shadow: 0 3px 10px rgba(17, 40, 61, 0.08);

  @media (max-width: 640px) {
    width: 100%;
    min-height: 40px;
    font-size: 0.82rem;
    padding: 0.48rem 0.6rem;

    ${({ $fullMobile }) =>
    $fullMobile &&
    css`
      grid-column: 1 / -1;
    `}
  }
`;

const ModalActionGroup = styled.div`
  display: flex;
  gap: 0.6rem;
  flex-wrap: wrap;
  justify-content: flex-end;

  @media (max-width: 640px) {
    display: contents;
  }
`;

const ReopenButton = styled.button`
  position: fixed;
  right: max(1rem, env(safe-area-inset-right));
  bottom: max(1rem, env(safe-area-inset-bottom));
  z-index: 8;
  border: 1px solid rgba(19, 43, 64, 0.2);
  border-radius: 999px;
  padding: 0.55rem 0.9rem;
  background: #ffffff;
  color: #1b3b57;
  font-size: 0.82rem;
  font-weight: 600;
  box-shadow: 0 8px 20px rgba(14, 32, 48, 0.18);
  cursor: pointer;
  min-height: 42px;
  touch-action: manipulation;

  @media (max-width: 560px) {
    padding: 0.52rem 0.78rem;
    font-size: 0.8rem;
    bottom: calc(env(safe-area-inset-bottom) + 8px);
  }
`;

const CookieManager = () => {
  const [consent, setConsent] = useState(() => cookieConsentService.getConsent());
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [draftPreferences, setDraftPreferences] = useState(() => cookieConsentService.getConsent().preferences);
  const modalBodyRef = useRef(null);

  useEffect(() => {
    if (typeof window === 'undefined') return undefined;

    const handleConsentChanged = (event) => {
      const nextConsent = event?.detail || cookieConsentService.getConsent();
      setConsent(nextConsent);
      setDraftPreferences(nextConsent.preferences);
    };

    const handleOpenManager = () => {
      const currentConsent = cookieConsentService.getConsent();
      setConsent(currentConsent);
      setDraftPreferences(currentConsent.preferences);
      setIsModalOpen(true);
    };

    window.addEventListener(COOKIE_CONSENT_CHANGED_EVENT, handleConsentChanged);
    window.addEventListener(COOKIE_MANAGER_OPEN_EVENT, handleOpenManager);

    return () => {
      window.removeEventListener(COOKIE_CONSENT_CHANGED_EVENT, handleConsentChanged);
      window.removeEventListener(COOKIE_MANAGER_OPEN_EVENT, handleOpenManager);
    };
  }, []);

  useEffect(() => {
    if (!isModalOpen || typeof window === 'undefined' || typeof document === 'undefined') {
      return undefined;
    }

    const { body, documentElement } = document;
    const scrollY = window.scrollY || 0;
    const prevBodyOverflow = body.style.overflow;
    const prevBodyPosition = body.style.position;
    const prevBodyTop = body.style.top;
    const prevBodyWidth = body.style.width;
    const prevBodyOverscroll = body.style.overscrollBehaviorY;
    const prevHtmlOverflow = documentElement.style.overflow;
    const prevHtmlOverscroll = documentElement.style.overscrollBehaviorY;

    body.style.overflow = 'hidden';
    body.style.position = 'fixed';
    body.style.top = `-${scrollY}px`;
    body.style.width = '100%';
    body.style.overscrollBehaviorY = 'none';
    documentElement.style.overflow = 'hidden';
    documentElement.style.overscrollBehaviorY = 'none';

    const preventBackgroundTouchScroll = (event) => {
      const target = event.target;
      if (!(target instanceof Node)) {
        event.preventDefault();
        return;
      }

      const canScrollModalContent = modalBodyRef.current?.contains(target);
      if (!canScrollModalContent) {
        event.preventDefault();
      }
    };

    document.addEventListener('touchmove', preventBackgroundTouchScroll, { passive: false });

    return () => {
      document.removeEventListener('touchmove', preventBackgroundTouchScroll);
      body.style.overflow = prevBodyOverflow;
      body.style.position = prevBodyPosition;
      body.style.top = prevBodyTop;
      body.style.width = prevBodyWidth;
      body.style.overscrollBehaviorY = prevBodyOverscroll;
      documentElement.style.overflow = prevHtmlOverflow;
      documentElement.style.overscrollBehaviorY = prevHtmlOverscroll;
      window.scrollTo(0, scrollY);
    };
  }, [isModalOpen]);

  const canCloseModal = true;

  const formattedUpdatedAt = useMemo(() => {
    if (!consent.updatedAt) return null;
    return new Date(consent.updatedAt).toLocaleString('fr-FR', {
      day: 'numeric',
      month: 'long',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  }, [consent.updatedAt]);

  const handleAcceptAll = () => {
    const nextConsent = cookieConsentService.acceptAll();
    setConsent(nextConsent);
    setDraftPreferences(nextConsent.preferences);
    setIsModalOpen(false);
  };

  const handleRejectOptional = () => {
    const nextConsent = cookieConsentService.rejectOptional();
    setConsent(nextConsent);
    setDraftPreferences(nextConsent.preferences);
    setIsModalOpen(false);
  };

  const handleSavePreferences = () => {
    const nextConsent = cookieConsentService.saveConsent({
      analytics: draftPreferences.analytics,
      marketing: draftPreferences.marketing
    });
    setConsent(nextConsent);
    setDraftPreferences(nextConsent.preferences);
    setIsModalOpen(false);
  };

  const openCustomization = () => {
    setDraftPreferences(consent.preferences);
    setIsModalOpen(true);
  };

  const handleOverlayClick = (event) => {
    if (event.target !== event.currentTarget) return;
    if (!canCloseModal) return;
    setIsModalOpen(false);
  };

  return (
    <>
      {consent.hasConsented && (
        <ReopenButton type="button" onClick={openCustomization} aria-label="Modifier mes préférences cookies">
          Cookies
        </ReopenButton>
      )}

      {!consent.hasConsented && (
        <Banner aria-live="polite" role="dialog" aria-label="Bannière de consentement aux cookies">
          <BannerInner>
            <div>
              <BannerTitle>Votre choix cookies</BannerTitle>
              <BannerText>
                {`${SITE_NAME} utilise des cookies essentiels pour fonctionner. Avec votre accord, nous activons aussi la mesure d'audience et les contenus marketing.`}
              </BannerText>
            </div>
            <BannerActions>
              <ActionButton type="button" $variant="ghost" onClick={openCustomization}>
                Personnaliser
              </ActionButton>
              <ActionButton type="button" $variant="secondary" onClick={handleRejectOptional}>
                Refuser les optionnels
              </ActionButton>
              <ActionButton type="button" onClick={handleAcceptAll}>
                Tout accepter
              </ActionButton>
            </BannerActions>
          </BannerInner>
        </Banner>
      )}

      {isModalOpen && (
        <Overlay onClick={handleOverlayClick}>
          <ModalPanel role="dialog" aria-modal="true" aria-label="Gestion des cookies">
            <ModalHeader>
              <ModalTitle>Gestion des cookies</ModalTitle>
              {canCloseModal && (
                <CloseButton type="button" onClick={() => setIsModalOpen(false)} aria-label="Fermer">
                  ×
                </CloseButton>
              )}
            </ModalHeader>
            <ModalBody ref={modalBodyRef}>
              <IntroText>
                Vous pouvez ajuster les cookies non essentiels ici. Les cookies techniques restent toujours actifs pour
                garantir la connexion, la sécurité et le bon fonctionnement de l&apos;application.
              </IntroText>

              <CookieRow>
                <span>
                  <CookieTitle>Cookies essentiels (obligatoires)</CookieTitle>
                  <CookieDescription>Connexion, sécurité, préférences de base.</CookieDescription>
                </span>
                <CookieToggle type="checkbox" checked disabled readOnly />
              </CookieRow>

              <CookieRow>
                <span>
                  <CookieTitle>Mesure d&apos;audience</CookieTitle>
                  <CookieDescription>Statistiques de fréquentation et navigation anonymisée.</CookieDescription>
                </span>
                <CookieToggle
                  type="checkbox"
                  checked={draftPreferences.analytics}
                  onChange={(event) => {
                    setDraftPreferences((prev) => ({ ...prev, analytics: event.target.checked }));
                  }}
                />
              </CookieRow>

              <CookieRow>
                <span>
                  <CookieTitle>Marketing</CookieTitle>
                  <CookieDescription>Personnalisation des contenus promotionnels.</CookieDescription>
                </span>
                <CookieToggle
                  type="checkbox"
                  checked={draftPreferences.marketing}
                  onChange={(event) => {
                    setDraftPreferences((prev) => ({ ...prev, marketing: event.target.checked }));
                  }}
                />
              </CookieRow>

              {formattedUpdatedAt && (
                <UpdatedAt>Dernière mise à jour de votre choix : {formattedUpdatedAt}</UpdatedAt>
              )}
            </ModalBody>
            <ModalFooter>
              <ModalAction type="button" $variant="ghost" onClick={handleRejectOptional}>
                Refuser les optionnels
              </ModalAction>
              <ModalActionGroup>
                <ModalAction type="button" $variant="ghost" onClick={handleAcceptAll}>
                  Tout accepter
                </ModalAction>
                <ModalAction type="button" $fullMobile onClick={handleSavePreferences}>
                  Enregistrer mes choix
                </ModalAction>
              </ModalActionGroup>
            </ModalFooter>
          </ModalPanel>
        </Overlay>
      )}
    </>
  );
};

export default CookieManager;
